/**
 * THE accepted-format list for deal client-context uploads (create panel, existing-deal upload
 * dialog, BPR session upload, Drive picker, API route gate, and the AI-chat /create-deal wizard).
 *
 * One list, so the door can never drift from what the server can read. The server-side readers
 * (apps/api/src/services/customers/document-parsers.ts + documents.ts) are FORMAT-level only:
 * unwrap the file, drop markup noise, keep all the words. The extension says how to read the
 * bytes, never what the document is.
 *
 * Deliberately NOT here: spreadsheets (.csv/.xlsx/...). The scope-spreadsheet import for
 * Continuous Improvement / BPR deals has its own list (SCOPE_FILE_EXTENSIONS) and is untouched.
 * The reference-estimate door (REFERENCE_ESTIMATE_*) also keeps its own list.
 */
const DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
/** Filename extensions (lowercase, leading dot) accepted as deal client context. */
export const DEAL_DOCUMENT_EXTENSIONS = [
    '.pdf',
    '.docx',
    '.txt',
    '.md',
    '.vtt',
    '.srt',
    '.json',
    '.yaml',
    '.yml',
    '.xml',
    '.html',
    '.htm',
    '.eml',
    '.pptx',
];
/** MIME types accepted as deal client context (MIME OR extension passes the gate). */
export const DEAL_DOCUMENT_MIME_TYPES = [
    'application/pdf',
    DOCX,
    'text/plain',
    'text/markdown',
    'text/x-markdown',
    'text/vtt',
    'application/json',
    'application/x-yaml',
    'application/yaml',
    'text/yaml',
    'text/x-yaml',
    'application/xml',
    'text/xml',
    'text/html',
    'message/rfc822',
    PPTX,
];
/**
 * Google-native editor types selectable from Drive. The server exports them on download:
 * Docs -> docx, Slides -> PDF (see GoogleDriveService.downloadFileForIngest). Sheets are
 * deliberately absent (spreadsheets are out of scope for this door).
 */
export const DEAL_DOCUMENT_DRIVE_EXTRA_MIME_TYPES = [
    'application/vnd.google-apps.document',
    'application/vnd.google-apps.presentation',
];
/** Drive picker MIME list: the device list plus Google Docs and Slides. */
export const DEAL_DOCUMENT_DRIVE_MIME_TYPES = [
    ...DEAL_DOCUMENT_MIME_TYPES,
    ...DEAL_DOCUMENT_DRIVE_EXTRA_MIME_TYPES,
];
/** Native `<input accept>` value for the deal-document file inputs. */
export const DEAL_DOCUMENT_ACCEPT_ATTR = DEAL_DOCUMENT_EXTENSIONS.join(',');
/** One muted line shown under a dropzone. Built from the list above - keep in step with it. */
export const DEAL_DOCUMENT_FORMATS_LABEL = 'PDF, Word, text, transcripts, emails (.eml), slides (.pptx), JSON, YAML, XML, web pages (.html)';
/**
 * Accept an uploaded deal document by MIME OR filename extension, case-insensitive. Browsers
 * often send application/octet-stream (or a blank type) for .eml / .yaml / .md, so the
 * extension is a first-class pass, not just a fallback.
 */
export function isAcceptedDealDocumentUpload(mimeType, fileName) {
    const mime = (mimeType ?? '').toLowerCase();
    if (DEAL_DOCUMENT_MIME_TYPES.includes(mime))
        return true;
    const lower = (fileName ?? '').toLowerCase();
    return DEAL_DOCUMENT_EXTENSIONS.some((ext) => lower.endsWith(ext));
}
//# sourceMappingURL=deal-document-upload.js.map